
import { IAutocompleteMatch } from "@directives/_forms/autocomplete/autocomplete.component";
import { IVeriMapComponent } from "@directives/_map/veriMap/veriMapComponent/veriMap.component";
import { IGeofence } from "@interfaces/IGeofences";
import { IGoogleMapsMarker } from "@interfaces/IGoogleMapsMarkerOptions";
import { AccountOption } from "@services/class/AccountOption";
import { ColourService } from "@services/colour.service";
import { GeocodeService } from "@services/geocode.service";
import { GoogleService } from "@services/google.service";
import { MapService } from "@services/map.service";
import { SessionService } from "@services/session.service";
import { StateService } from "@uirouter/angularjs";
import * as angular from "angular";
import { SettingsName } from "../../../../class/SettingsName";
import { IRouteStop } from "../interfaces/IRouteStop";
import "./routePlanner.scss";

export interface IRouteWaypoint {
    id: number;
    label?: string;
    latitude?: number;
    longitude?: number;

    geofenceId?: number;

    visible?: boolean;
    color?: string;
    disableEdit?: boolean;
}

export interface IRouteResult {
    summary?: string;
    duration?: string;
    durationInTraffic?: string;
    distance?: number;
    distanceWithSuffix?: string;
    estimatedCost?: number;
    legs?: google.maps.DirectionsLeg[];
    mapUrl?: string;
}

interface IParsedDirectionsResult {
    numRoutes?: number;
}

class RoutePlannerController implements angular.IComponentController {

    showOptions = false;
    showOptimisationLoader = false;

    routeResults: IRouteResult[] = [];
    selectedRoute: IRouteResult = null;
    waypoints: IRouteWaypoint[] = [];

    ignoreNextUpdate = false;

    onEditGeofenceClick: () => void;
    onRouteStopsChanged: (args: { $routeStops: IRouteStop[] }) => void;

    optimisedDirectionsResult: google.maps.DirectionsResult;

    errorText = "";

    geocodedAddWayPointAddress: string;
    currentPopupLocation: google.maps.LatLng;

    popupMapPosition: google.maps.LatLng;
    popupMapGeofence: IGeofence;

    costMultiplier = 0.11;
    loading = false;
    returnToStart = false;

    routeStops: IRouteStop[];
    includePlaces: boolean;

    focusIndex = 0;

    showAlternativeRoutes: boolean;

    static $inject = ["$q", "$state", "$timeout", "$mdDialog", "googleService", "geocodeService", "mapService", "sessionService", "colourService"];

    constructor(
        private $q: angular.IQService,
        private $state: StateService,
        private $timeout: angular.ITimeoutService,
        private $mdDialog: angular.material.IDialogService,
        private googleService: GoogleService,
        private geocodeService: GeocodeService,
        private mapService: MapService,
        private sessionService: SessionService,
        private colourService: ColourService) {
    }

    $onInit(): void {

        const accountOptions = this.sessionService.getItem<AccountOption>(SettingsName.AccountOptions);
        this.costMultiplier = parseFloat(accountOptions.JourneyCostValue);

        if (accountOptions.JourneyCostType === "Mileage") {
            // convert from cost per mile into cost per km
            this.costMultiplier /= 1.60934;
        }
    }

    $onChanges(onChangesObj: angular.IOnChangesObject): void {

        if (onChangesObj["routeStops"]) {

            if (this.ignoreNextUpdate) {
                this.ignoreNextUpdate = false;
                return;
            }

            this.waypoints = [];

            if (this.routeStops && this.routeStops.length) {

                // Need to create waypoints from routestops
                this.waypoints = [];
                this.routeStops.forEach(routeStop => {

                    const waypoint: IRouteWaypoint = {
                        id: Math.round(Math.random() * 10000000),
                        label: routeStop.Label || routeStop.displayLabel,
                        visible: true,
                        disableEdit: routeStop.disableEdit
                    };

                    if (routeStop.GeocodedAddress) {
                        waypoint.label = waypoint.label || routeStop.GeocodedAddress.FormattedAddress;
                        waypoint.latitude = routeStop.GeocodedAddress.CenterPoint.Latitude;
                        waypoint.longitude = routeStop.GeocodedAddress.CenterPoint.Longitude;
                    }
                    else if (routeStop.Geofence) {
                        waypoint.label = waypoint.label || routeStop.Geofence.Description;
                        waypoint.latitude = routeStop.Geofence.Latitude;
                        waypoint.longitude = routeStop.Geofence.Longitude;
                        waypoint.geofenceId = routeStop.Geofence.Id;
                    }

                    routeStop.waypoint = waypoint;

                    this.waypoints.push(waypoint);
                });

                this.returnToStart = this.doesRouteEndAtTheStart();

                this.calculateJourney(false);
            }

            this.ensureMinimumWaypoints();
        }
    }

    closeDialog() {
        this.onEditGeofenceClick();
    }

    onMapLoad(veriMap: IVeriMapComponent) {
        this.ensureMinimumWaypoints();

        if (this.$state.params["startingPosition"]) {
            const position = this.$state.params["startingPosition"] as google.maps.LatLng;

            this.addWaypoint(position);
        }
    }

    private doesRouteEndAtTheStart() {
        const lastIndex = this.waypoints.length - 1;
        if (this.waypoints[0].latitude === this.waypoints[lastIndex].latitude &&
            this.waypoints[0].longitude === this.waypoints[lastIndex].longitude) {
            return true;
        }

        return false;
    }

    selectedMatchChange(waypoint: IRouteWaypoint, $match: IAutocompleteMatch) {

        if ($match) {
            waypoint.label = $match.label;

            if ($match.location) {
                waypoint.latitude = $match.location.lat;
                waypoint.longitude = $match.location.lng;
            }

            if ($match.source === "Geofence") {
                waypoint.geofenceId = $match.objectId;
            }

            waypoint.visible = true;
            this.waypoints[this.waypoints.indexOf(waypoint)] = angular.copy(waypoint);
        }
        else {
            waypoint.visible = false;
            waypoint.latitude = null;
            waypoint.longitude = null;
        }

        this.calculateJourney(true);

        this.focusIndex = this.waypoints.length - 1;
    }

    addNewWaypoint(): IRouteWaypoint {
        const waypoint: IRouteWaypoint = {
            label: "",
            id: Math.round(Math.random() * 10000000)
        };

        waypoint.visible = true;

        this.addWayPointToList(waypoint);

        return waypoint;
    }

    removeWaypoint(waypoint: IRouteWaypoint) {
        const index = this.waypoints.indexOf(waypoint);
        const endItem = this.waypoints.length - 1;

        if (index == 0 && this.returnToStart) {

            this.waypoints.splice(endItem, 1);
            this.waypoints.splice(index, 1);

            this.waypoints.push(this.waypoints[0]);

            this.returnToStart = false;
            this.returnToStartChanged();
        }
        else {
            this.waypoints.splice(index, 1);
        }

        this.calculateJourney(true);
    }

    returnToStartChanged() {
        if (this.doesRouteEndAtTheStart() && !this.returnToStart) {

            this.waypoints.pop();

            if (this.waypoints.length < 2) {
                this.addNewWaypoint();
            }
        }
        else {
            var lastWaypoint = this.waypoints[this.waypoints.length - 1];

            if (!lastWaypoint.latitude || !lastWaypoint.longitude || !lastWaypoint.label) {
                this.waypoints.pop();
            }

            this.waypoints.push(angular.copy(this.waypoints[0]));
        }

        this.calculateJourney(true);
    }

    moveWaypointUp(waypoint: IRouteWaypoint) {
        const currentPosition = this.waypoints.indexOf(waypoint);
        const newPosition = currentPosition - 1;

        if (newPosition < 0) {
            return;
        }

        this.moveWayPoint(currentPosition, newPosition);
    }

    moveWaypointDown(waypoint: IRouteWaypoint) {
        const currentPosition = this.waypoints.indexOf(waypoint);
        const newPosition = currentPosition + 1;

        if (newPosition > this.waypoints.length - 1) {
            return;
        }

        this.moveWayPoint(currentPosition, newPosition);
    }

    addWaypoint(position: google.maps.LatLng) {
        const waypoint = this.addNewWaypoint();

        const lat = position.lat();
        const lng = position.lng();

        var geocodedAddress = "New Waypoint";

        this.geocodeService
            .getAddressFromPoint(position)
            .then(location => {
                geocodedAddress = location.LocationDescription;
            })
            .finally(() => {
                this.selectedMatchChange(
                    waypoint,
                    {
                        label: geocodedAddress,
                        location:
                        {
                            lat: lat,
                            lng: lng
                        },
                        icon: "",
                        source: "Map"
                    })
            })
    }

    onMarkerMoved($marker: IGoogleMapsMarker<IRouteStop>) {
        this.geocodeService
            .getAddressFromPoint(new google.maps.LatLng($marker.options.position))
            .then(location => {

                // Find our local copy
                this.waypoints.forEach(waypoint => {

                    if (waypoint.id === $marker.model.waypoint.id) {

                        waypoint.geofenceId = null;
                        waypoint.latitude = $marker.options.position.lat;
                        waypoint.longitude = $marker.options.position.lng;
                        waypoint.label = location.LocationDescription;
                    }
                });

                this.calculateJourney(true);
            });
    }

    newRouteSelected(route: IRouteResult) {
        this.selectedRoute = route;
        this.createRouteStops();
    }

    mapGeofenceClicked(geofence: IGeofence, leftClick: boolean) {
        this.popupMapGeofence = geofence;
        this.popupMapPosition = new google.maps.LatLng(geofence.Latitude, geofence.Longitude);
    }

    addGeofenceWaypointAtPopupLocation() {
        const waypoint = this.addNewWaypoint();
        const match: IAutocompleteMatch = {
            objectId: this.popupMapGeofence.Id,
            label: this.popupMapGeofence.Description,
            source: "Geofence",
            location:
            {
                lat: this.popupMapGeofence.Latitude,
                lng: this.popupMapGeofence.Longitude
            },
            icon: null
        };

        this.selectedMatchChange(waypoint, match);

        this.popupMapGeofence = null;
        this.popupMapPosition = null;
    }

    private ensureMinimumWaypoints() {
        // We need at least two waypoints, for our default start and end
        // We only add them once the map has been initialised as the auto complete boxes depend on google maps
        while (this.waypoints.length < 2) {
            this.addNewWaypoint();
        }
    }

    optimiseRoute() {
        this.showOptimisationLoader = true;

        const directionsPromise = this.getDirectionsFromGoogle(true);

        this.$q
            .all([directionsPromise, this.$timeout(1000)])
            .then(([result, blank]) => {

                this.optimisedDirectionsResult = result;

                console.log(this.optimisedDirectionsResult);

                this.showOptimisationResultDialog();

                this.showOptimisationLoader = false;
            });
    }

    private showOptimisationResultDialog() {

        if (this.routeOrderDifferentFromInput(this.optimisedDirectionsResult.routes[0])) {
            const flattenedLeg = this.googleService.flattenLegs(this.optimisedDirectionsResult.routes[0].legs);

            const dialog = this.$mdDialog.confirm()
                .title("An optimal route has been found")
                .htmlContent("A new optimal route has been found<br /><br />" +
                    `Original Route: <strong>${this.routeResults[0].distanceWithSuffix} / ${this.routeResults[0].duration}</strong><br />` +
                    `New Route: <strong>${flattenedLeg.distance.text} / ${flattenedLeg.duration.text}</strong><br /><br />` +
                    "Would you like to use this new route?")
                .ok("Accept")
                .cancel("Cancel");

            this.$mdDialog.show(dialog).then(() => {
                this.useOptimalRoute();
            });
        }
        else {
            const dialog = this.$mdDialog.alert()
                .title("Current route is optimal")
                .textContent("The current route is optimal and no further changes are required")
                .ok("Close");

            this.$mdDialog.show(dialog);
        }
    }

    colorWayPoints() {
        this.waypoints.forEach((waypoint, i) => {
            waypoint.color = this.colourService.getColour(i, true).solidColour;
        });
    }

    private useOptimalRoute() {

        const newWaypointOrder = this.optimisedDirectionsResult.routes[0].waypoint_order;

        var newWaypoints: IRouteWaypoint[] = [];

        // Our start and end points always stay the same, only the waypoints in the middle get optimised
        newWaypoints[0] = this.waypoints[0];

        newWaypointOrder.forEach((newIndex, oldIndex) => {
            newWaypoints[newIndex + 1] = this.waypoints[oldIndex + 1];
        });

        const endIndex = this.waypoints.length - 1;
        newWaypoints.push(this.waypoints[endIndex]);

        this.waypoints = newWaypoints;
        this.calculateJourney(true);
    }

    private addWayPointToList(waypoint: IRouteWaypoint) {
        const invisibleWayPoints = this.waypoints.filter(wp => !wp.visible || (!wp.latitude && !wp.longitude));

        const first = invisibleWayPoints.length > 0 ? invisibleWayPoints[0] : null;

        if (this.returnToStart) {
            this.waypoints.splice(this.waypoints.length - 1, 0, waypoint);
        }
        else if (!first || this.waypoints.length < 2) {
            this.waypoints.push(waypoint);
        }
        else {
            const firstIndex = this.waypoints.indexOf(first);
            this.waypoints[firstIndex] = waypoint;
        }

        this.colorWayPoints();
    }

    private moveWayPoint(fromPosition: number, toPosition: number) {
        this.waypoints.splice(fromPosition, 0, this.waypoints.splice(toPosition, 1)[0]);

        this.calculateJourney(true);
        this.colorWayPoints();
    }

    private getVisibleWaypoints(): IRouteWaypoint[] {
        const waypoints = this.waypoints.filter(waypoint => waypoint.visible && !!waypoint.latitude && !!waypoint.longitude);

        return waypoints;
    }

    private clearExistingJourneys() {

        this.routeResults = [];
    }

    private getDirectionsFromGoogle(optimise = false): angular.IPromise<google.maps.DirectionsResult> {
        const latlngWaypoints: google.maps.LatLng[] = [];

        this.getVisibleWaypoints().forEach(waypoint => {
            // Protects against issues with gaps in the route
            latlngWaypoints.push(new google.maps.LatLng(waypoint.latitude, waypoint.longitude));
        });

        if (latlngWaypoints.length <= 1) {
            return this.$q.resolve(null);
        }

        const start = latlngWaypoints.shift();
        const end = latlngWaypoints.pop();

        return this.googleService.getDirections(start, end, latlngWaypoints, optimise);
    }

    private calculateJourney(updateRouteStops: boolean) {

        this.colorWayPoints();

        this.errorText = "";

        this.loading = true;
        this.getDirectionsFromGoogle()
            .then(directionResult => {

                this.selectedRoute = null;

                if (!directionResult) {
                    return;
                }

                this.clearExistingJourneys();

                directionResult.routes.forEach((route, i) => {

                    this.routeResults.push(this.convertGoogleRouteToRouteResult(route));
                });

                this.selectedRoute = this.routeResults[0];
            })
            .catch((status: google.maps.DirectionsStatus) => {
                this.handleDirectionsError(status);
            })
            .finally(() => {
                if (updateRouteStops) {
                    this.createRouteStops();
                }

                this.loading = false;
            });
    }

    private convertGoogleRouteToRouteResult(directionsRoute: google.maps.DirectionsRoute): IRouteResult {
        const routeLeg = this.googleService.flattenLegs(directionsRoute.legs);

        return {
            summary: `via ${(directionsRoute as any).summary}`,
            distance: routeLeg.distance.value,
            distanceWithSuffix: routeLeg.distance.text,
            duration: routeLeg.duration.text,
            durationInTraffic: routeLeg.duration_in_traffic
                ? `${routeLeg.duration_in_traffic.text} with traffic`
                : `${routeLeg.duration.text} without traffic`,
            // The x100 /100 gives you one decimal place on the round
            estimatedCost: Math.round(100 * this.costMultiplier * routeLeg.distance.value / 1000) / 100,
            legs: directionsRoute.legs
        }
    }

    private createRouteStops() {
        if (this.getVisibleWaypoints().length === 0) {
            return;
        }

        this.routeStops = [];
        this.ignoreNextUpdate = true;

        const startStop: IRouteStop = {};
        if (this.waypoints[0] && this.waypoints[0].latitude && this.waypoints[0].longitude) {
            this.setRouteStopInformation(this.waypoints[0], startStop);
        }
        else if (this.waypoints[1] && this.waypoints[1].latitude && this.waypoints[1].longitude) {
            this.setRouteStopInformation(this.waypoints[1], startStop);
        }

        this.routeStops.push(startStop);

        if (!this.selectedRoute) {
            return;
        }

        this.selectedRoute.legs.forEach((leg, i) => {
            // The first leg is actually the first stop, so we need to add + 1 so we don't get the depot
            const wayPointIndex = i + 1;

            // This accounts for going back to the original depot
            var waypoint = this.waypoints[wayPointIndex];
            if (!waypoint) {
                waypoint = this.waypoints[0];
            }

            var travelTime = (!leg.duration.value ? 0 : leg.duration.value) / 60;

            var routeStop: IRouteStop = {
                TravelDistanceMetres: leg.distance.value,
                TravelTime: this.convertMinsToTimeSpan(travelTime),
                travelTimeMins: travelTime
            };

            this.setRouteStopInformation(waypoint, routeStop);

            let lastLinePoint: number[] = null;
            leg.steps.forEach(step => {
                step.path.forEach(latlng => {
                    const newLinePoint = [latlng.lat(), latlng.lng()];

                    if (!lastLinePoint || lastLinePoint[0] !== newLinePoint[0] || lastLinePoint[1] !== newLinePoint[1]) {
                        routeStop.LinePoints.push(newLinePoint);
                    }

                    lastLinePoint = newLinePoint;
                });
            });

            routeStop.LinePoints = this.mapService.simplifyRoute(routeStop.LinePoints, 0.00001, true);

            this.routeStops.push(routeStop);
        });

        if (this.onRouteStopsChanged) {
            var validStops: IRouteStop[] = [];

            this.routeStops.forEach(stop => {
                if (!stop.waypoint.latitude || !stop.waypoint.longitude) {
                    return false;
                }

                return validStops.push(stop);
            });

            this.onRouteStopsChanged({ $routeStops: validStops });
        }
    }

    private convertMinsToTimeSpan(totalMins: number): string {
        const hours = Math.floor(totalMins / 60);

        totalMins %= 60;
        const minutes = Math.round(totalMins);

        return `${hours}:${minutes}`;
    }

    private setRouteStopInformation(waypoint: IRouteWaypoint, routeStop: IRouteStop) {

        routeStop.Id = Math.round(Math.random() * 10000000);
        routeStop.Label = waypoint.label;
        routeStop.LinePoints = [];
        routeStop.displayLabel = waypoint.label;
        routeStop.waypoint = waypoint;

        if (waypoint.geofenceId) {
            routeStop.GeofenceId = waypoint.geofenceId;

            routeStop.Geofence = {
                Id: waypoint.geofenceId,
                Latitude: waypoint.latitude,
                Longitude: waypoint.longitude
            };
        }
        else {
            routeStop.GeocodedAddress = {
                CenterPoint: {
                    Latitude: waypoint.latitude,
                    Longitude: waypoint.longitude
                }
            };
        }
    }

    private routeOrderDifferentFromInput(route: google.maps.DirectionsRoute): boolean {

        if (route.waypoint_order.length === 0) {
            return false;
        }

        for (let i = 0; i < route.waypoint_order.length; i++) {

            if (i !== route.waypoint_order[i]) {
                return true;
            }
        }

        return false;
    }

    private handleDirectionsError(status: google.maps.DirectionsStatus) {
        this.clearExistingJourneys();

        switch (status) {
            case google.maps.DirectionsStatus.OK:
                break;

            case google.maps.DirectionsStatus.MAX_WAYPOINTS_EXCEEDED:
                this.errorText = "Max 23 waypoints";
                break;

            case google.maps.DirectionsStatus.ZERO_RESULTS:
                this.errorText = "A route does not exist between waypoints";
                break;

            case google.maps.DirectionsStatus.NOT_FOUND:
                this.errorText = "No routes found";
                break;

            default:
                this.errorText = "Unknown error";
        }

        console.error("handleDirectionsError", this.errorText, status);
    }
}

export class RoutePlanningComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            includePlaces: "<",
            routeStops: "<",
            showAlternativeRoutes: "<",
            showOptimiseRoutes: "<",
            onRouteStopsChanged: "&?",
            onEditGeofenceClick: "&"
        };

        this.controller = RoutePlannerController;
        this.template = require("./routeplanner.html");
    }
}
